const getGoogleClientId = (): string => import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '';

type PendingGoogleAuth = {
  intent: 'login' | 'register';
  nickname?: string;
  role?: 'employer' | 'helper';
  locale?: 'zh-Hant' | 'zh-Hans' | 'en' | 'id' | 'fil';
  pairingCode?: string;
  pairInviteMode?: boolean;
  fromPath?: string;
  nonce: string;
};

type PendingGoogleRegistration = {
  idToken: string;
  email: string;
  fromPath?: string;
  pairInviteMode?: boolean;
  pairingCode?: string;
};

const generateNonce = (): string => `${Date.now()}-${Math.random().toString(36).slice(2)}`;
const PENDING_GOOGLE_REGISTER_KEY = 'fdh.pendingGoogleRegister';

const encodeState = (value: PendingGoogleAuth): string => {
  const json = JSON.stringify(value);
  return btoa(unescape(encodeURIComponent(json)));
};

const decodeState = (value: string): PendingGoogleAuth => {
  const json = decodeURIComponent(escape(atob(value)));
  return JSON.parse(json) as PendingGoogleAuth;
};

const decodeJwtPayload = (jwt: string): Record<string, unknown> => {
  const parts = jwt.split('.');
  if (parts.length < 2) {
    throw new Error('Invalid token format');
  }
  const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const json = decodeURIComponent(
    Array.from(atob(padded))
      .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
      .join('')
  );
  return JSON.parse(json) as Record<string, unknown>;
};

export const getGoogleEmailFromIdToken = (idToken: string): string => {
  const payload = decodeJwtPayload(idToken);
  return typeof payload.email === 'string' ? payload.email : '';
};

export const hasGoogleClientId = (): boolean => getGoogleClientId().trim().length > 0;

export const startGoogleRedirectAuth = (pending: Omit<PendingGoogleAuth, 'nonce'>): void => {
  const clientId = getGoogleClientId().trim();
  if (!clientId) {
    throw new Error('Missing VITE_GOOGLE_CLIENT_ID');
  }
  const nonce = generateNonce();
  const state: PendingGoogleAuth = {
    ...pending,
    nonce,
  };
  const redirectUri = `${window.location.origin}/auth/google/callback`;
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'id_token',
    scope: 'openid email profile',
    nonce,
    state: encodeState(state),
    prompt: 'select_account',
  });
  window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
};

export const consumeGoogleRedirectAuth = (): {
  idToken: string;
  pending: PendingGoogleAuth;
} => {
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  const hashParams = new URLSearchParams(hash);
  const stateRaw = hashParams.get('state');
  if (!stateRaw) {
    throw new Error('Missing Google auth context. Please retry.');
  }
  const pending = decodeState(stateRaw);
  const idToken = hashParams.get('id_token');
  if (!idToken) {
    const err = hashParams.get('error') ?? 'missing_id_token';
    throw new Error(`Google redirect did not return token (${err})`);
  }
  const payload = decodeJwtPayload(idToken);
  const tokenNonce = typeof payload.nonce === 'string' ? payload.nonce : '';
  if (!tokenNonce || tokenNonce !== pending.nonce) {
    throw new Error('Google nonce mismatch. Please retry.');
  }
  return { idToken, pending };
};

export const stashPendingGoogleRegistration = (payload: PendingGoogleRegistration): void => {
  sessionStorage.setItem(PENDING_GOOGLE_REGISTER_KEY, JSON.stringify(payload));
};

export const readPendingGoogleRegistration = (): PendingGoogleRegistration | null => {
  const raw = sessionStorage.getItem(PENDING_GOOGLE_REGISTER_KEY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as PendingGoogleRegistration;
  } catch {
    return null;
  }
};

export const clearPendingGoogleRegistration = (): void => {
  sessionStorage.removeItem(PENDING_GOOGLE_REGISTER_KEY);
};
